import { useEffect, useState } from "react";
import type { Conversation } from "@atrium/shared";
import { cn } from "../../lib/cn";
import { getSocket } from "../../lib/socket";
import { Avatar } from "../../components/ui/Avatar";
import { Button, IconButton } from "../../components/ui/Button";
import { toast } from "../../components/ui/Toast";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconBell, IconCall } from "../../components/icons";
import { useMe } from "../auth/AuthProvider";
import { useCall, useVoice } from "./VoiceProvider";
import { RoomGrid } from "./RoomGrid";
import { VoiceControls, useShareBlockedReason } from "./VoiceControls";
import { useCallStore } from "./stores";

function useElapsed(since: string | undefined) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [since]);
  if (!since) return "";
  const s = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

/** Header button: start a call, or join the one in progress. */
export function CallButton({ conversation, title }: { conversation: Conversation; title: string }) {
  const voice = useVoice();
  const call = useCall(conversation.id);
  const inCall = voice.callId === conversation.id;
  if (inCall) return null;
  return (
    <IconButton
      label={call ? "Join call" : "Start a call"}
      icon={IconCall}
      disabled={voice.status === "connecting"}
      onClick={() => void voice.joinCall(conversation.id, title)}
      className={cn(call && "text-success")}
    />
  );
}

/** The call above a conversation's messages: who's in it, their screens, and your controls. */
export function CallPanel({ conversation, title }: { conversation: Conversation; title: string }) {
  const voice = useVoice();
  const me = useMe();
  const call = useCall(conversation.id);
  const inCall = voice.callId === conversation.id;
  const connecting = inCall && voice.status === "connecting";
  const ringingMe = useCallStore((s) => s.incoming.some((i) => i.conversationId === conversation.id));
  const elapsed = useElapsed(call?.startedAt);
  const shareReason = useShareBlockedReason();

  if (!call && !connecting) return null;
  const participants = call?.participants ?? [];
  const inCallIds = new Set(participants.map((p) => p.user.id));
  const ringing = conversation.members.filter((m) => call?.ringing.includes(m.id) && m.id !== me.id);
  const missing = conversation.members.filter((m) => m.id !== me.id && !inCallIds.has(m.id) && !call?.ringing.includes(m.id));

  const ringAgain = () =>
    getSocket()?.emit("call:ring", { conversationId: conversation.id }, (res) => {
      if (!res.ok) toast.error(res.error);
    });

  if (!inCall) {
    return (
      <section aria-label="Call in progress" className="flex shrink-0 items-center gap-3 border-b border-line bg-bg-subtle px-4 py-2.5">
        <IconCall className="icon size-5 text-success" aria-hidden />
        <div className="flex -space-x-2">
          {participants.slice(0, 5).map((p) => (
            <Avatar key={p.socketId} src={p.user.avatarUrl} name={p.user.displayName} size="sm" className="ring-2 ring-bg-subtle" />
          ))}
        </div>
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-medium">{ringingMe ? "Incoming call" : "Call in progress"}</span>
          <span className="ml-2 text-fg-3 tabular">{elapsed}</span>
        </div>
        {ringingMe && (
          <Button variant="ghost" onClick={() => {
            getSocket()?.emit("call:decline", { conversationId: conversation.id });
            useCallStore.getState().stopRing(conversation.id);
          }}>
            Decline
          </Button>
        )}
        <Button variant="primary" icon={IconCall} disabled={voice.status === "connecting"} onClick={() => void voice.joinCall(conversation.id, title)}>
          Join call
        </Button>
      </section>
    );
  }

  return (
    <section aria-label="Call" className="flex max-h-[60%] shrink-0 flex-col gap-4 overflow-y-auto border-b border-line bg-bg-subtle px-4 py-4">
      <div className="flex items-center gap-2 text-sm">
        <IconCall className="icon size-5 text-success" aria-hidden />
        <span className="font-medium">{connecting ? "Connecting…" : "In call"}</span>
        <span className="text-fg-3 tabular">{elapsed}</span>
        {ringing.length > 0 && (
          <span className="ml-auto flex items-center gap-1.5 text-fg-3">
            <span className="flex -space-x-1.5">
              {ringing.slice(0, 4).map((u) => (
                <Avatar key={u.id} src={u.avatarUrl} name={u.displayName} size="xs" className="opacity-60 ring-2 ring-bg-subtle motion-safe:animate-shimmer" />
              ))}
            </span>
            Ringing…
          </span>
        )}
        {ringing.length === 0 && missing.length > 0 && (
          <Tooltip label={`Ring ${missing.map((m) => m.displayName).join(", ")}`}>
            <Button variant="ghost" size="sm" icon={IconBell} className="ml-auto" onClick={ringAgain}>
              Ring
            </Button>
          </Tooltip>
        )}
      </div>
      {participants.length > 0 && <RoomGrid participants={participants} compact />}
      <VoiceControls shareReason={shareReason} />
    </section>
  );
}
